import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import { connectionsListUrl } from './linkedinUrls'
import { LinkedinSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Automations

enum StageKey {
  LINKEDIN_CONNECTIONS_LIST_GATHER = 'LINKEDIN_CONNECTIONS_LIST_GATHER',
  LINKEDIN_CONNECTIONS_LIST_UPDATE = 'LINKEDIN_CONNECTIONS_LIST_UPDATE',
  LINKEDIN_CONNECTIONS_LIST_UNDO = 'LINKEDIN_CONNECTIONS_LIST_UNDO',
}

const CONNECTIONS_LIST_SETTINGS = {
  CONNECTIONS_LIST: 'Connection visibility',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_CONNECTIONS_LIST_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: connectionsListUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        CONNECTIONS_LIST_SETTINGS,
        LinkedinSettingKey.CONNECTIONS_LIST,
        LinkedinRecommendationKey.LINKEDIN_CONNECTIONS_LIST,
        'p',
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_CONNECTIONS_LIST_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: connectionsListUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext

      const currentSetting = await getCurrentValueForCheckbox(
        CONNECTIONS_LIST_SETTINGS.CONNECTIONS_LIST,
        'p',
      )

      // Toggle off if currently on
      if (currentSetting && !isDry) {
        return toggleCheckbox(CONNECTIONS_LIST_SETTINGS.CONNECTIONS_LIST, 'p')
      }
      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_CONNECTIONS_LIST_UNDO,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: connectionsListUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const currentSetting = await getCurrentValueForCheckbox(
        CONNECTIONS_LIST_SETTINGS.CONNECTIONS_LIST,
        'p',
      )

      // Toggle setting if checkbox does not match initial values
      if (
        initialValues?.[LinkedinSettingKey.CONNECTIONS_LIST] !==
          currentSetting &&
        !isDry
      ) {
        return toggleCheckbox(
          CONNECTIONS_LIST_SETTINGS[LinkedinSettingKey.CONNECTIONS_LIST],
          'p',
        )
      }
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
